/**
 * Défauts sectoriels — référentiel `secteur → défauts intelligents` appliqués en
 * silence par la génération de site et le wizard (tâches 🟡). Cf.
 * docs/onboarding.html § 04, § 05, § 12, § 13 et docs/onboarding-implementation-plan.md § 12.
 *
 * Volontairement coarse-grained : ~10 secteurs plutôt que ~150 métiers. Le
 * métier saisi par le pro est résolu vers un secteur via `resolveSector()`.
 */

/** Secteur d'activité — granularité des défauts. */
export type OnboardingSector =
  | 'beaute'
  | 'bien_etre'
  | 'vtc'
  | 'coach_sport'
  | 'restauration'
  | 'artisan'
  | 'medical'
  | 'photographe'
  | 'conseil'
  | 'generic';

/**
 * Liste des secteurs valides — source unique pour les validations TypeBox et les
 * dropdowns (ex. CRUD admin du référentiel de métiers). Tenue alignée avec le
 * type `OnboardingSector` ci-dessus.
 */
export const ONBOARDING_SECTOR_VALUES: readonly OnboardingSector[] = [
  'beaute',
  'bien_etre',
  'vtc',
  'coach_sport',
  'restauration',
  'artisan',
  'medical',
  'photographe',
  'conseil',
  'generic',
];

/** Un jour de la grille hebdomadaire pré-remplie. */
export interface SectoralOpeningDay {
  open: boolean;
  /** Heure d'ouverture au format `HH:mm` — absent si `open` est faux. */
  start?: string;
  /** Heure de fermeture au format `HH:mm` — absent si `open` est faux. */
  end?: string;
}

/** Défauts intelligents pour un secteur donné. */
export interface SectoralDefault {
  /** Durée par défaut d'une prestation, en minutes (tâche 🟡 durée). */
  default_prestation_duration_min: number;
  /** Grille hebdomadaire pré-remplie — index 0 = lundi … 6 = dimanche. */
  opening_hours: SectoralOpeningDay[];
  /** Les packs / formules sont-ils pertinents pour ce secteur ? */
  packs_relevant: boolean;
  /** Les cartes cadeaux sont-elles pertinentes ? */
  gift_cards_relevant: boolean;
  /** Les ateliers / réservations de groupe sont-ils pertinents ? */
  workshops_relevant: boolean;
  /** Identifiant de palette sectorielle pour la génération du site. */
  palette: string;
}

const CLOSED: SectoralOpeningDay = { open: false };
const day = (start: string, end: string): SectoralOpeningDay => ({ open: true, start, end });

/** Grilles hebdomadaires types — ordre lundi → dimanche. */
const WEEK = {
  beaute: [
    CLOSED,
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    CLOSED,
  ],
  bien_etre: [
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '13:00'),
    CLOSED,
  ],
  vtc: [
    day('06:00', '23:00'),
    day('06:00', '23:00'),
    day('06:00', '23:00'),
    day('06:00', '23:00'),
    day('06:00', '23:00'),
    day('06:00', '23:00'),
    day('06:00', '23:00'),
  ],
  coach_sport: [
    day('07:00', '21:00'),
    day('07:00', '21:00'),
    day('07:00', '21:00'),
    day('07:00', '21:00'),
    day('07:00', '21:00'),
    day('07:00', '21:00'),
    CLOSED,
  ],
  restauration: [
    day('09:00', '22:00'),
    day('09:00', '22:00'),
    day('09:00', '22:00'),
    day('09:00', '22:00'),
    day('09:00', '22:00'),
    day('09:00', '22:00'),
    CLOSED,
  ],
  weekdays_8_18: [
    day('08:00', '18:00'),
    day('08:00', '18:00'),
    day('08:00', '18:00'),
    day('08:00', '18:00'),
    day('08:00', '18:00'),
    CLOSED,
    CLOSED,
  ],
  weekdays_9_19: [
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    CLOSED,
    CLOSED,
  ],
  weekdays_9_18: [
    day('09:00', '18:00'),
    day('09:00', '18:00'),
    day('09:00', '18:00'),
    day('09:00', '18:00'),
    day('09:00', '18:00'),
    CLOSED,
    CLOSED,
  ],
  mon_sat_9_19: [
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    day('09:00', '19:00'),
    CLOSED,
  ],
} satisfies Record<string, SectoralOpeningDay[]>;

export const SECTORAL_DEFAULTS: Record<OnboardingSector, SectoralDefault> = {
  beaute: {
    default_prestation_duration_min: 60,
    opening_hours: WEEK.beaute,
    packs_relevant: true,
    gift_cards_relevant: true,
    workshops_relevant: false,
    palette: 'palette_beaute',
  },
  bien_etre: {
    default_prestation_duration_min: 60,
    opening_hours: WEEK.bien_etre,
    packs_relevant: true,
    gift_cards_relevant: true,
    workshops_relevant: true,
    palette: 'palette_bien_etre',
  },
  vtc: {
    default_prestation_duration_min: 30,
    opening_hours: WEEK.vtc,
    packs_relevant: false,
    gift_cards_relevant: false,
    workshops_relevant: false,
    palette: 'palette_vtc',
  },
  coach_sport: {
    default_prestation_duration_min: 60,
    opening_hours: WEEK.coach_sport,
    packs_relevant: true,
    gift_cards_relevant: true,
    workshops_relevant: true,
    palette: 'palette_coach_sport',
  },
  restauration: {
    default_prestation_duration_min: 90,
    opening_hours: WEEK.restauration,
    packs_relevant: true,
    gift_cards_relevant: true,
    workshops_relevant: true,
    palette: 'palette_restauration',
  },
  artisan: {
    default_prestation_duration_min: 60,
    opening_hours: WEEK.weekdays_8_18,
    packs_relevant: false,
    gift_cards_relevant: false,
    workshops_relevant: false,
    palette: 'palette_artisan',
  },
  medical: {
    default_prestation_duration_min: 30,
    opening_hours: WEEK.weekdays_9_19,
    packs_relevant: false,
    gift_cards_relevant: false,
    workshops_relevant: false,
    palette: 'palette_medical',
  },
  photographe: {
    default_prestation_duration_min: 90,
    opening_hours: WEEK.mon_sat_9_19,
    packs_relevant: true,
    gift_cards_relevant: true,
    workshops_relevant: true,
    palette: 'palette_photographe',
  },
  conseil: {
    default_prestation_duration_min: 60,
    opening_hours: WEEK.weekdays_9_18,
    packs_relevant: false,
    gift_cards_relevant: false,
    workshops_relevant: false,
    palette: 'palette_conseil',
  },
  generic: {
    default_prestation_duration_min: 60,
    opening_hours: WEEK.weekdays_9_18,
    packs_relevant: true,
    gift_cards_relevant: false,
    workshops_relevant: false,
    palette: 'palette_generic',
  },
};

/** Mots-clés de résolution `métier → secteur` (ordre = priorité). */
const SECTOR_KEYWORDS: { sector: OnboardingSector; keywords: string[] }[] = [
  {
    sector: 'beaute',
    keywords: [
      'coiff',
      'estheti',
      'esthéti',
      'ongle',
      'manucure',
      'maquill',
      'beaut',
      'barbier',
      'épilation',
      'epilation',
      'ongulaire',
      'tatou',
      'tattoo',
      'piercing',
      'microblading',
    ],
  },
  {
    sector: 'bien_etre',
    keywords: [
      'massage',
      'masseur',
      'spa',
      'bien-être',
      'bien etre',
      'sophro',
      'naturopath',
      'reflexo',
      'réflexo',
      'yoga',
      'relax',
      'hypno',
      'reiki',
      'shiatsu',
      'energ',
      'énerg',
      'medita',
      'médita',
      'acupunct',
      'kinesiolog',
      'kinésiolog',
      'etiopath',
      'étiopath',
      'grapho',
      'art-therap',
      'art-thérap',
      'art therap',
      'perinat',
      'périnat',
      'postnatal',
      'doula',
      'magnetis',
      'magnétis',
      'chinoise',
      'diété',
      'diete',
      'nutrition',
      'reboute',
      'sexolog',
    ],
  },
  { sector: 'vtc', keywords: ['vtc', 'taxi', 'chauffeur', 'transport', 'course'] },
  {
    sector: 'coach_sport',
    keywords: ['coach', 'sport', 'fitness', 'personal trainer', 'préparateur', 'preparateur', 'pilates'],
  },
  { sector: 'restauration', keywords: ['restaur', 'traiteur', 'chef', 'cuisin', 'bar', 'café', 'cafe', 'food'] },
  {
    sector: 'medical',
    // Para-médical sur RENDEZ-VOUS uniquement (clients potentiels). Médecin / dentiste / infirmier
    // (réglementé, territoire Doctolib) sont volontairement EXCLUS → ils retombent sur `generic`.
    keywords: ['kiné', 'kine', 'osteo', 'ostéo', 'psycho', 'sage-femme', 'sage femme', 'maïeut', 'maieut'],
  },
  { sector: 'photographe', keywords: ['photo', 'vidéaste', 'videaste', 'cameraman'] },
  { sector: 'conseil', keywords: ['conseil', 'consultant', 'coach business', 'avocat', 'comptable', 'expert'] },
  {
    sector: 'artisan',
    keywords: [
      'artisan',
      'plomb',
      'électric',
      'electric',
      'menuis',
      'maçon',
      'macon',
      'btp',
      'peintre',
      'serrur',
      'bricol',
    ],
  },
];

/**
 * Résout un libellé de métier saisi par le pro vers un secteur. Renvoie
 * `'generic'` si aucun mot-clé ne correspond.
 */
export const resolveSector = (metier: string | null | undefined): OnboardingSector => {
  if (!metier) return 'generic';
  // underscores → espaces : un libellé/slug type `centre_bien_etre` doit matcher le mot-clé « bien etre ».
  const normalized = metier.toLowerCase().replace(/_/g, ' ').trim();
  for (const { sector, keywords } of SECTOR_KEYWORDS) {
    if (keywords.some((keyword): boolean => normalized.includes(keyword))) {
      return sector;
    }
  }
  return 'generic';
};

/** Renvoie les défauts sectoriels résolus pour un libellé de métier. */
export const getSectoralDefault = (metier: string | null | undefined): SectoralDefault =>
  SECTORAL_DEFAULTS[resolveSector(metier)];
